import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import type { Theme } from '@material-ui/core';
import { Container, CssBaseline, Paper } from '@material-ui/core';
import { useLayoutContext } from './LayoutContext';
import { useSelector } from 'react-redux';
import { Sidebar } from '../Sidebar';
import {
    environmentMenuItems,
    keyFiguresMenuItems,
    menuItems,
    projectMenuItems,
} from '@forvalter/components/MenuItems';
import { Header } from '../Header';
import type { AppDefinition } from '@klp/core/app/classes/AppDefinition';
import { useCurrentUser } from '@klp/auth';
import { useEffect } from 'react';
import { isMobileScreen } from '@klp/core/app/config';
import { ByggSelector, contextSelectors } from '@klp/contextSelector/index';
import classNames from 'classnames';
import { theme as klpTheme } from '@klp/design/Theme';
import { useReadElementHeight } from '@klp/layout/hooks/useReadElementHeight';
import { Breadcrumbs } from '@klp/breadcrumbs/Breadcrumbs';
import { getRequiredConfig } from '@klpkonsern/appconfig';
import { getSuperofficeLink } from '@forvalter/packages/superoffice';

const layoutStyles = (theme: Theme) => ({
    mainLayout: {
        display: 'flex',
        flex: '1 1 auto',
        '& .MainContent': {
            paddingBottom: theme.mainContent.paddingBottom,
        },
    },
    mainContainer: {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column',
    },

    withBackground: {
        backgroundColor: theme.palette.primary.veryLight,
    },
    paper: {
        display: 'flex',
        flexDirection: 'column',
        padding: theme.spacing(3),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
        minHeight: `calc(100vh - ${theme.spacing(6)}px)`,
        width: '100%',
    },
    main: {
        transition: theme.layout.transition,
        width: '100%',
        marginLeft: 0,
        marginRight: 0,
        willChange: 'width, margin-left',

        '.IsDesktop.Centered &': {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '& .Paper': {
                justifyContent: 'center',
            },
        },

        '.IsDesktop.WithHeader &': {
            '& .Paper': {
                minHeight: `calc(100vh - ${theme.spacing(6)}px - ${theme.header.height}px)`,
            },
        },
        '.IsDesktop.Constrained &': {
            maxWidth: `${theme.breakpoints.values.xl}px`,
            width: '100%',
            marginLeft: 'auto',
            marginRight: 'auto',
        },
        '.IsDesktop.WithSidebar &': {
            maxWidth: `calc(100vw - var(--bygg-selector-closed-width) - var(--sidebar-closed-width) - ${theme.spacing(2)}px)`,
        },
        '.IsDesktop.WithSidebar.Constrained &': {
            width: '100%',
            maxWidth: `min(${theme.breakpoints.values.xl}px, calc(100vw - var(--sidebar-closed-width) - var(--bygg-selector-closed-width) - ${theme.spacing(2)}px))`,
        },

        [theme.breakpoints.down('lg')]: {
            '.IsDesktop &': {
                maxWidth: '100vw',
            },
            '.IsDesktop.Constrained &': {
                marginLeft: 0,
                marginRight: 0,
            },
            '.IsDesktop.WithSidebar.ByggSelectorOpen &': {
                //maxWidth: `calc(100% + var(--bygg-selector-open-width) - var(--bygg-selector-closed-width))`,
                width: `calc(100% + var(--bygg-selector-open-width) - var(--bygg-selector-closed-width))`,
                marginLeft: `calc((var(--bygg-selector-open-width) - var(--bygg-selector-closed-width)) * -1)`,
            },
            '.IsDesktop.WithSidebar.MenuOpen &': {
                //maxwidth: `calc(100% + calc(var(--sidebar-open-width) - var(--sidebar-closed-width)))`,
                width: `calc(100% + calc(var(--sidebar-open-width) - var(--sidebar-closed-width)))`,
                marginLeft: `calc((var(--sidebar-open-width) - var(--sidebar-closed-width)) * -1)`,
            },
            '.IsDesktop.WithSidebar.MenuPin &': {
                width: `100%`,
                marginLeft: 0,
            },
            '.IsDesktop.WithSidebar.MenuOpen.ByggSelectorOpen &': {
                //maxWidth: `calc(100% + var(--sidebar-open-width) - var(--sidebar-closed-width) + var(--bygg-selector-open-width) - var(--sidebar-closed-width))`,
                width: `calc(100% + var(--sidebar-open-width) - var(--sidebar-closed-width) + var(--bygg-selector-open-width) - var(--sidebar-closed-width))`,
                marginLeft: `calc((var(--sidebar-open-width) - var(--sidebar-closed-width) + var(--bygg-selector-open-width) - var(--sidebar-closed-width)) * -1)`,
            },
            '.IsDesktop.WithSidebar.MenuPin.ByggSelectorOpen &': {
                //maxWidth: `calc(100% + calc( var(--bygg-selector-open-width) - var(--sidebar-closed-width)))`,
                width: `calc(100% + calc( var(--bygg-selector-open-width) - var(--sidebar-closed-width)))`,
                marginLeft: `calc((var(--bygg-selector-open-width) - var(--sidebar-closed-width)) * -1)`,
            },
        },
    },
});

const calculateLeftOffset = (
    menuPin: boolean,
    withoutSidebar: boolean,
): number =>
    withoutSidebar
        ? 0
        : menuPin
          ? klpTheme.sidebar.open.width + klpTheme.byggSelector.closed.width
          : klpTheme.sidebar.closed.width + klpTheme.byggSelector.closed.width;

const calculateTopOffset = (withoutHeader: boolean): number =>
    withoutHeader ? 0 : klpTheme.header.height + 1;

export const LayoutContent = (props: React.PropsWithChildren<unknown>) => {
    const { t, i18n } = useTranslation();
    const classes = useKlpStyles({}, layoutStyles);
    const {
        withoutHeader,
        withoutSidebar,
        centered,
        fullScreen,
        withBackground,
        menuOpen,
        menuPin,
        isMobile,
    } = useLayoutContext();
    const { openMineBygg } = useSelector(contextSelectors);

    const { isAdmin } = useCurrentUser();

    const getLocalizedKundeportalUrl = () => {
        switch (i18n.language) {
            case 'da':
                return getRequiredConfig('WEBAPP_APP_DANISH_KUNDEPORTAL_URL');
            case 'sv':
                return getRequiredConfig('WEBAPP_APP_SWEDISH_KUNDEPORTAL_URL');
            default:
                return getRequiredConfig('WEBAPP_APP_KUNDEPORTAL_URL');
        }
    };

    const appSelection: Array<AppDefinition> = [
        {
            name: t('app.forvalter'),
            url: window.location.origin,
        },
        {
            name: t('app.mittleieforhold'),
            url: getLocalizedKundeportalUrl(),
        },
        {
            name: t('app.eiendomGpt'),
            url: getRequiredConfig('WEBAPP_APP_EIENDOM_GPT_URL'),
            openInNewTab: true,
        },
    ];

    if (isAdmin) {
        appSelection.push(
            {
                name: t('app.openInSuperOffice'),
                url: getSuperofficeLink(),
                openInNewTab: true,
            },
            {
                name: t('app.verified'),
                url: getRequiredConfig('WEBAPP_APP_VERIFIED_LINK'),
                openInNewTab: true,
            },
            {
                name: t('app.dynamics365'),
                url: getRequiredConfig('WEBAPP_APP_DYNAMICS365_LINK'),
                openInNewTab: true,
            },
        );
    }

    const { setIsMobile } = useLayoutContext();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(isMobileScreen());
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [setIsMobile]);

    const mainClasses = classNames(classes.mainContainer, {
        [classes.withBackground]: withBackground,
    });

    const layoutLeftOffset = React.useMemo(
        () => calculateLeftOffset(menuPin, withoutSidebar),
        [menuPin, withoutSidebar],
    );
    const layoutTopOffset = React.useMemo(
        () => calculateTopOffset(withoutHeader),
        [withoutHeader],
    );

    const mainContentRef = useRef<HTMLDivElement>(null);
    useReadElementHeight(mainContentRef, '--bygg-selector-height');

    const { setLayoutCssProperty } = useLayoutContext();

    useEffect(() => {
        setLayoutCssProperty([
            {
                name: '--sidebar-open-width',
                value: `${klpTheme.sidebar.open.width}px`,
            },
            {
                name: '--sidebar-closed-width',
                value: `${klpTheme.sidebar.closed.width}px`,
            },
            {
                name: '--bygg-selector-open-width',
                value: `${klpTheme.byggSelector.open.width}px`,
            },
            {
                name: '--bygg-selector-closed-width',
                value: `${klpTheme.byggSelector.closed.width}px`,
            },
            { name: '--layout-left-offset', value: `${layoutLeftOffset}px` },
            { name: '--layout-top-offset', value: `${layoutTopOffset}px` },
        ]);

        return () => {
            setLayoutCssProperty([
                { name: '--sidebar-open-width', value: '' },
                { name: '--sidebar-closed-width', value: '' },
                { name: '--bygg-selector-open-width', value: '' },
                { name: '--bygg-selector-closed-width', value: '' },
                { name: '--layout-left-offset', value: '' },
                { name: '--layout-top-offset', value: '' },
                { name: '--bygg-selector-height', value: '' },
            ]);
        };
    }, [layoutLeftOffset, layoutTopOffset, setLayoutCssProperty]);

    return (
        <div
            className={classNames('Layout', classes.mainLayout, {
                WithHeader: !withoutHeader,
                WithSidebar: !withoutSidebar,
                MenuOpen: menuOpen,
                MenuClosed: !menuOpen,
                MenuPin: menuPin,
                ByggSelectorOpen: openMineBygg,
                FullScreen: fullScreen,
                Constrained: !fullScreen,
                IsMobile: isMobile,
                IsDesktop: !isMobile,
                Centered: centered,
            })}
        >
            <CssBaseline />
            {!withoutSidebar && (
                <>
                    <Sidebar
                        menuItems={menuItems}
                        environmentMenuItems={environmentMenuItems}
                        keyFiguresMenuItems={keyFiguresMenuItems}
                        projectMenuItems={projectMenuItems}
                        data-testid="Sidebar"
                    />
                    <ByggSelector />
                </>
            )}
            <Container
                className={mainClasses}
                data-testid="MainContainer"
                disableGutters={true}
                maxWidth={false}
            >
                {!withoutHeader && (
                    <Header
                        appSelection={appSelection}
                        breadcrumbsComponent={Breadcrumbs}
                    />
                )}
                <Container
                    data-testid="Main"
                    className={classNames('Main', classes.main)}
                    component="main"
                    maxWidth={false}
                    ref={mainContentRef}
                >
                    <Paper className={classNames('Paper', classes.paper)}>
                        {props.children ?? <></>}
                    </Paper>
                </Container>
            </Container>
        </div>
    );
};
